/* Staggered collection grid — "option C, column stack" (Maud, 2026-09-07).
 *
 * WHAT IT DOES. Lets each card rise to meet the card above it in its own column
 * instead of waiting for the tallest card in its row, WITHOUT changing which
 * column a card lands in. Card 1,2,3,4 across the top, 5,6,7,8 directly beneath,
 * exactly as today - only the row edges stagger.
 *
 * WHY NOT CSS. Chrome 152 supports none of `grid-template-rows: masonry`,
 * `item-flow`, `masonry-auto-flow` or `display: masonry` (measured, 2026-09-04).
 * The CSS-columns fallback fills DOWNWARDS, which is what made masonry read
 * 1,3,5,7 across the top row and got it rejected on 2026-09-01. So this is JS.
 *
 * SIX THINGS THAT ARE LOAD-BEARING:
 *
 * 1. THE CSS STILL OWNS THE BREAKPOINTS. The column count is read back out of
 *    the computed `grid-template-columns`, never re-declared here. Change the
 *    media queries in tyt-margherita.css and this follows automatically - there
 *    is no second copy of "4 above 990, 2 above 600, 1 below" to drift.
 *
 * 2. ONE COLUMN DETACHES COMPLETELY. A single stack has no neighbour to wait
 *    for, so there is nothing to close - measured identical to today at 375px.
 *    Phones get none of this code's cost, and the CSS grid is handed back
 *    untouched.
 *
 * 3. HIDDEN CARDS ARE SKIPPED, NOT POSITIONED. tyt-collection-filter.js hides
 *    with `display:none` for both the search and the "Show more" batch, and the
 *    column slot is taken from position among the VISIBLE cards. So a search
 *    that matches 5 trails lays them out 1,2,3,4 / 5 - balanced and in order -
 *    rather than leaving holes where the hidden ones used to be.
 *
 * 4. READS ARE BATCHED AWAY FROM WRITES. Every width is written, then every
 *    height is read, then every position is written. Interleaving them forces a
 *    fresh layout PER CARD, which on 195 cards is the difference between one
 *    reflow and 195 of them.
 *
 * 5. IT DEGRADES TO TODAY. With JavaScript off nothing runs, the cards stay in
 *    normal grid flow, and the page is exactly the strict-row grid it is now.
 *
 * 6. EVERY PASS RELEASES THE GRID BEFORE IT READS IT. Not tidiness, and not a
 *    cost worth optimising away: Safari reports a different track list for a
 *    grid whose children this file has already taken out of flow, so a pass
 *    that skips the release lays the cards out too wide. Added 2026-09-17
 *    after exactly that shipped. See trackList() for the measured numbers.
 *
 * SCOPE — WIDENED 2026-09-16, and this is the decision the old note asked for.
 * It used to run on `[data-tyt-grid-flow]` only, which was the COLLECTION grid
 * alone. Maud found the cart's "Room for one more" band leaving a landscape
 * poster in a card as tall as its portrait neighbour, with the dead space
 * inside the card: *"this isn't the case in the Trails section is it?"* It was
 * not — the trails page had this script and nothing else did. It now runs on
 * every `.tyt-grid--cards`: the collection grid, the homepage teaser, the cart
 * band, the 404 grid, the About gallery, the product page's recommendations and
 * the search results. The attribute is still honoured so nothing that sets it
 * breaks.
 *
 * TWO NEIGHBOURS IT HAS TO CO-OPERATE WITH, both of which change what is
 * VISIBLE after this has already laid out:
 *   - tyt-shuffle-grid.js DELETES cards from the cart and 404 bands.
 *   - tyt-grid-rows.js hides whatever does not fill a whole row, and re-runs on
 *     resize and whenever a recommendations fetch lands.
 * Rather than depend on script order, tyt-grid-rows.js calls back into
 * `window.tytGridFlow.layout()` at the end of every trim, and a shuffle removes
 * children, which trips that same trim through its MutationObserver. So the
 * last word always belongs to whoever changed the grid.
 */
(function () {
  'use strict';

  /* Loaded once from the layout since 2026-09-16, but a section that still
   * emits its own tag must not start a second copy: two sets of resize
   * listeners would both write positions. */
  if (window.tytGridFlow) return;

  /* ⚠ THE GAP IS READ FROM THE GRID, NOT ASSUMED. It used to be a hardcoded
   * `var GAP = 24 /* --gap-md *​/`, which was true only for the collection
   * grid — the only grid this ran on until 2026-09-16. Maud's two-up phone
   * strip (`.tyt-grid--phone2`) uses `gap: 14px`, so the moment this started
   * running on the cart and 404 bands the right-hand column was placed 10px too
   * far right: two 153px tracks spread to 330px inside a 320px box. Nothing
   * reached the edge of the PAGE, which is exactly why an overflow test would
   * not have caught it. Column and row gaps are read separately because CSS
   * allows them to differ, even though every grid here sets both at once. */
  function gapsOf(grid) {
    var cs = window.getComputedStyle(grid);
    var col = parseFloat(cs.columnGap);
    var row = parseFloat(cs.rowGap);
    return {
      col: isFinite(col) ? col : 24,
      row: isFinite(row) ? row : 24
    };
  }

  function gridsOf() {
    return document.querySelectorAll('[data-tyt-grid-flow], .tyt-grid--cards');
  }

  /* The CSS is the single source of truth for the columns — how many AND how
   * wide. Returns the resolved track list in pixels, e.g. [364, 364, 364, 364].
   *
   * ⚠ THIS USED TO RETURN ONLY THE COUNT, AND THE WIDTH WAS RE-DERIVED BELOW AS
   * (clientWidth - gaps) / cols. That was correct only while the grid was
   * `repeat(N, 1fr)`, where the tracks always consume the full width. Since
   * 2026-09-07 the product grids are
   * `repeat(auto-fill, minmax(min(300px,100%), 1fr))`, and re-deriving the
   * width would stretch the cards back out to fill the container — silently
   * undoing the fixed card width whenever JavaScript was on, while a no-JS
   * page rendered it correctly. Reading the real tracks keeps the promise the
   * file header already makes: there is no second copy of the layout here.
   *
   * ⚠ THE GRID MUST HAVE ITS CARDS IN NORMAL FLOW WHEN THIS IS CALLED, AND
   * `layout()` BELOW RELEASES IT FIRST TO GUARANTEE THAT. This comment used to
   * say the opposite — that getComputedStyle reports the full track list even
   * with no in-flow children at all, "verified in Chrome against auto-fill".
   * Chrome does. WEBKIT DOES NOT, and that one sentence is what shipped the
   * bug and would have talked the next reader out of re-checking.
   *
   * Measured on an iPad Pro 11 (834x1194, Safari 26.5), 2026-09-17, on the
   * homepage band at a 762px content box:
   *     cards in flow      -> reports 369, 369   (correct, and what it drew)
   *     cards absolute     -> reports 381, 381   (wrong: 762/2, gaps ignored)
   * So the FIRST pass was always right and every later one — fonts.ready,
   * window.load, every resize and rotate — re-read a grid this file had
   * already emptied, believed 381, and wrote cards 24px too wide. Three
   * columns in landscape reported 374 for 358 and put the last card 12px off
   * the screen. Safari on a Mac is the same engine, so this was never an iPad
   * bug. Diagnosis and the device numbers:
   * ../../2026-09-17_safari-grid-flow-overflow.md */
  function trackList(grid) {
    var tpl = window.getComputedStyle(grid).gridTemplateColumns;
    if (!tpl || tpl === 'none') return null;
    var parts = tpl.split(' ').filter(function (t) { return t.length > 0; });
    if (!parts.length) return null;
    var px = [];
    for (var i = 0; i < parts.length; i++) {
      var n = parseFloat(parts[i]);
      if (!isFinite(n) || n <= 0) return null;   /* a keyword we cannot use */
      px.push(n);
    }
    return px;
  }

  /* Hand the grid back to CSS: used at one column and if anything goes wrong. */
  function release(grid) {
    var cards = grid.querySelectorAll('[data-tyt-product-card]');
    grid.style.position = '';
    grid.style.height = '';
    for (var i = 0; i < cards.length; i++) {
      var s = cards[i].style;
      s.position = ''; s.left = ''; s.top = ''; s.width = '';
    }
    grid.removeAttribute('data-tyt-grid-flow-on');
  }

  function layout(grid) {
    /* ⚠ RELEASE BEFORE READING, ALWAYS — THIS IS THE FIX FOR THE SAFARI
     * OVERFLOW AND IT IS THE WHOLE OF IT (2026-09-17).
     * Every pass has to read the tracks in the state the CSS describes, which
     * means the cards back in normal flow. The first pass got that for free;
     * the second onwards inherited the absolute positions this function had
     * just written, and Safari then reports a different track list (see
     * trackList above). Releasing first makes every pass identical to the
     * first one.
     *
     * IT COSTS NOTHING. The function already forces two layout flushes — one
     * to read the tracks, one to read the heights — and this keeps it at two;
     * the clear below simply dirties the tree before the read that was
     * happening anyway. Nothing paints in between, because the whole function
     * runs inside one task and writes the positions back before it yields, so
     * there is no flash and no scroll jump.
     *
     * `release` deliberately does NOT clear `display`, so the cards that
     * tyt-collection-filter.js has hidden stay hidden and the skip below still
     * sees them. */
    release(grid);

    var tracks = trackList(grid);
    var gaps = gapsOf(grid);

    /* No usable track list, or one column: nothing to close. Give the grid
     * back to CSS entirely. */
    if (!tracks || tracks.length < 2) { release(grid); return; }

    var cols = tracks.length;
    var cards = grid.querySelectorAll('[data-tyt-product-card]');
    var width = grid.clientWidth;
    if (!width) return;                       /* not laid out yet (display:none ancestor) */

    var cardWidth = tracks[0];

    /* Where the first column starts. With `1fr` tracks this is always 0,
     * because they consume every pixel — but a capped track width would leave
     * the row narrower than the grid, and `justify-content` would centre it.
     * Computing it means the JS follows the CSS instead of assuming it. */
    var used = cols * cardWidth + gaps.col * (cols - 1);

    /* ⚠ A ROW WIDER THAN THE GRID MEANS THE NUMBERS ARE WRONG, NOT THAT THE
     * DESIGN IS. The tracks either fill the grid (`1fr`) or leave slack to be
     * centred — they can never need MORE room than the grid has, so if they
     * do, whatever we just read cannot be trusted and nothing here can make it
     * right. Hand the grid back to CSS: a strict-row grid that is not
     * staggered is a small loss, and cards hanging off the side of the page is
     * not. This is the guard that would have stopped the Safari fault reaching
     * a device at all, so it stays even though the release above should now
     * keep it from ever firing. 1px of tolerance for subpixel track widths. */
    if (used > width + 1) { release(grid); return; }

    var offset = (width - used) / 2;
    if (!(offset > 0)) offset = 0;

    var visible = [];
    var i, s;

    /* WRITE pass — take the visible cards out of flow at their final width. */
    for (i = 0; i < cards.length; i++) {
      s = cards[i].style;
      if (s.display === 'none') { s.position = ''; s.left = ''; s.top = ''; s.width = ''; continue; }
      s.position = 'absolute';
      s.width = cardWidth + 'px';
      visible.push(cards[i]);
    }

    /* READ pass — every height at once, so this costs ONE reflow, not N. */
    var heights = [];
    for (i = 0; i < visible.length; i++) heights.push(visible[i].offsetHeight);

    /* WRITE pass — round-robin column, so reading order is exactly today's. */
    var colHeight = [];
    for (i = 0; i < cols; i++) colHeight.push(0);
    for (i = 0; i < visible.length; i++) {
      var col = i % cols;
      s = visible[i].style;
      s.left = (offset + col * (cardWidth + gaps.col)) + 'px';
      s.top = colHeight[col] + 'px';
      colHeight[col] += heights[i] + gaps.row;
    }

    var tallest = 0;
    for (i = 0; i < cols; i++) if (colHeight[i] > tallest) tallest = colHeight[i];

    grid.style.position = 'relative';
    grid.style.height = (visible.length ? tallest - gaps.row : 0) + 'px';
    grid.setAttribute('data-tyt-grid-flow-on', String(cols));
  }

  function layoutAll() {
    var grids = gridsOf();
    for (var i = 0; i < grids.length; i++) layout(grids[i]);
  }

  function init() {
    var grids = gridsOf();
    for (var i = 0; i < grids.length; i++) {
      /* tyt-collection-filter.js calls this after every render - the search,
       * each "Show more" batch, and every background page it appends. Without
       * it the grid would keep the positions it had before the filter ran. */
      grids[i]._tytOnRender = layoutAll;
    }
    layoutAll();
  }

  /* Fraunces changes how many lines a trail name takes, which changes card
   * heights - so lay out again once the real font is in. */
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(layoutAll);

  /* And once more at load. A card whose height settles AFTER the first pass -
   * a title that rewraps, a badge that arrives, anything the initial pass could
   * not know - would otherwise leave the grid at a height computed from the old
   * numbers, with the bottom row hanging past it. Measured on the cart band at
   * 360px, where the two-up phone strip puts flow in charge for the first time:
   * one card grew 14px and the grid stayed 14px short. Cheap - one pass over
   * grids that are already positioned. */
  window.addEventListener('load', layoutAll);

  var t;
  window.addEventListener('resize', function () {
    clearTimeout(t);
    t = setTimeout(layoutAll, 120);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);

  window.tytGridFlow = { layout: layoutAll, release: function () {
    var g = gridsOf(); for (var i = 0; i < g.length; i++) release(g[i]);
  } };
})();
